import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiOutlineTrash } from "react-icons/hi";
import { BsChevronDown, BsChevronRight } from "react-icons/bs";
import InputField from "components/common/Input/Input";
import SwitchWithIcon from "components/common/SwitchWithIcon";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { parseBooleanVal } from "Utils/utils";
import useCareLabels from "hooks/useCareLabels";

const createEmptyTask = () => ({
  clientId: `task-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  id: null,
  description: "",
  is_mandatory: false,
});

const normalizeTask = (task, index) => ({
  clientId: task?.id ?? task?.uuid ?? `task-${index}`,
  id: task?.id ?? null,
  description: (task?.description ?? task?.task ?? "").trim(),
  is_mandatory: parseBooleanVal(task?.is_mandatory ?? task?.is_mandatory_task),
});

const getNonEmptyTasks = (taskList) =>
  taskList.filter((task) => String(task.description ?? task.task ?? "").trim());

export const serializeParticipantTasks = (taskList) =>
  getNonEmptyTasks(taskList).map((task) => ({
    task: String(task.description ?? task.task ?? "").trim(),
    is_mandatory: parseBooleanVal(task.is_mandatory),
  }));

export const getParticipantTasksSnapshot = (taskList) =>
  JSON.stringify(serializeParticipantTasks(taskList));

export const appendParticipantTasksToFormData = (formData, tasks) => {
  if (!Array.isArray(tasks)) return;

  if (tasks.length === 0) {
    formData.append("tasks[0][task]", "");
    formData.append("tasks[0][is_mandatory]", "0");
    return;
  }

  tasks.forEach((task, index) => {
    formData.append(`tasks[${index}][task]`, String(task.task ?? "").trim());
    formData.append(
      `tasks[${index}][is_mandatory]`,
      task.is_mandatory ? "1" : "0"
    );
  });
};

export const resolveSettingTasks = (source) => {
  const raw =
    source?.tasks ?? source?.participant_tasks ?? source?.participantTasks;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw.toJS === "function") return raw.toJS();
  if (typeof raw.toArray === "function") return raw.toArray();
  return [];
};

const ensureEditableTaskRows = (taskList, canEdit) => {
  if (canEdit && taskList.length === 0) {
    return [createEmptyTask()];
  }
  return taskList;
};

const ParticipantShiftTasksNotes = forwardRef(
  (
    {
      settingTasks = [],
      isUserGracePeriodOver = false,
      canEdit = false,
    },
    ref
  ) => {
    const { participantLabel } = useCareLabels();
    const [tasks, setTasks] = useState([]);
    const [initialTasksSnapshot, setInitialTasksSnapshot] = useState("[]");
    const [isLoading, setIsLoading] = useState(true);
    const [isExpanded, setIsExpanded] = useState(true);

    const isReadOnly = !canEdit || isUserGracePeriodOver;
    const lastAppliedTasksKeyRef = useRef(null);

    const incomingTasksKey = useMemo(
      () => getParticipantTasksSnapshot(resolveSettingTasks({ tasks: settingTasks })),
      [settingTasks]
    );

    const applySettingData = useCallback(
      (rawTasks) => {
        const normalizedTasks = resolveSettingTasks({ tasks: rawTasks }).map(
          (task, index) => normalizeTask(task, index)
        );
        const loadedTasks = ensureEditableTaskRows(normalizedTasks, canEdit);
        setTasks(loadedTasks);
        setInitialTasksSnapshot(getParticipantTasksSnapshot(normalizedTasks));
      },
      [canEdit]
    );

    const hasTasksChanged = useCallback(
      () => getParticipantTasksSnapshot(tasks) !== initialTasksSnapshot,
      [tasks, initialTasksSnapshot]
    );

    const getChangedTasksPayload = useCallback(() => {
      if (!hasTasksChanged()) {
        return null;
      }
      return serializeParticipantTasks(tasks);
    }, [hasTasksChanged, tasks]);

    const validateTasks = useCallback(() => null, []);

    useImperativeHandle(ref, () => ({
      hasTasksChanged,
      getChangedTasksPayload,
      validateTasks,
    }));

    useEffect(() => {
      if (lastAppliedTasksKeyRef.current === incomingTasksKey) {
        setIsLoading(false);
        return;
      }

      lastAppliedTasksKeyRef.current = incomingTasksKey;
      applySettingData(settingTasks);
      setIsLoading(false);
    }, [applySettingData, incomingTasksKey, settingTasks]);

    const handleTaskChange = (clientId, value) => {
      setTasks((prev) =>
        prev.map((task) =>
          task.clientId === clientId ? { ...task, description: value } : task
        )
      );
    };

    const handleMandatoryChange = (clientId, value) => {
      if (isReadOnly) return;
      setTasks((prev) =>
        prev.map((task) =>
          task.clientId === clientId ? { ...task, is_mandatory: value } : task
        )
      );
    };

    const handleAddTask = () => {
      if (isReadOnly) return;
      setTasks((prev) => [...prev, createEmptyTask()]);
    };

    const handleRemoveTask = (clientId) => {
      if (isReadOnly) return;
      setTasks((prev) => {
        const updated = prev.filter((task) => task.clientId !== clientId);
        return ensureEditableTaskRows(updated, canEdit);
      });
    };

    return (
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-2.5 mt-2'>
        <button
          type='button'
          onClick={() => setIsExpanded((prev) => !prev)}
          className='flex items-center gap-1.5 mb-2 w-full text-left'
          aria-expanded={isExpanded}
          aria-label={
            isExpanded
              ? "Collapse Default Shift Tasks and Notes"
              : "Expand Default Shift Tasks and Notes"
          }
        >
          {isExpanded ? (
            <BsChevronDown className='w-4 h-4 text-gray-500 flex-shrink-0' />
          ) : (
            <BsChevronRight className='w-4 h-4 text-gray-500 flex-shrink-0' />
          )}
          <div className='w-6 h-6 rounded bg-orange-100 flex items-center justify-center flex-shrink-0'>
            <svg
              className='w-4 h-4 text-orange-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4'
              />
            </svg>
          </div>
          <h3 className='text-xs font-bold text-gray-900'>
            Default Shift Tasks and Notes
          </h3>
        </button>

        {isExpanded && (
          <>
            {isLoading ? (
              <div className='flex justify-center items-center py-6'>
                <CircularLoader
                  classes='flex justify-center items-center'
                  classloader='loader-m'
                />
              </div>
            ) : (
              <>
                {tasks.length === 0 && isReadOnly ? (
                  <p className='text-sm text-gray-500 py-2'>
                    No tasks configured for this {participantLabel}.
                  </p>
                ) : (
                  tasks.map((task, index) => (
                    <div
                      key={task.clientId}
                      className='flex items-center gap-2 mb-1.5'
                    >
                      <div className='flex-1 min-w-0'>
                        <InputField
                          readOnly={isReadOnly}
                          placeHolder='Add your tasks here...'
                          value={task.description || task.task || ""}
                          handleChange={(value) =>
                            handleTaskChange(task.clientId, value)
                          }
                          type='text'
                          name={`participant_task[${index}]`}
                        />
                      </div>

                      <div className='flex-shrink-0 pt-1'>
                        <SwitchWithIcon
                          isSmall
                          checked={parseBooleanVal(task.is_mandatory)}
                          onChange={(value) =>
                            handleMandatoryChange(task.clientId, value)
                          }
                          disabled={isReadOnly}
                          tooltip='Mandatory task'
                        />
                      </div>

                      {!isReadOnly && (
                        <div className='pt-3 flex-shrink-0'>
                          <div
                            onClick={() => handleRemoveTask(task.clientId)}
                            tabIndex={0}
                            role='button'
                            className='icon-delete-wrapper'
                            title='Delete Task'
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleRemoveTask(task.clientId);
                              }
                            }}
                          >
                            <HiOutlineTrash className='icon-size' />
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}

                {!isReadOnly && (
                  <div className='mt-1'>
                    <button
                      type='button'
                      onClick={handleAddTask}
                      className='text-[#4B6CB7] text-xs font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      + Add More Tasks
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  }
);

ParticipantShiftTasksNotes.displayName = "ParticipantShiftTasksNotes";

export default ParticipantShiftTasksNotes;
